import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import ReservationModal from '@/react-app/components/ReservationModal';
import { usePermissions, useIsOwner } from '@/react-app/hooks/usePermissions';
import Layout from '@/react-app/components/Layout';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Calendar,
  CreditCard,
  AlertTriangle,
  Home,
  Package,
  PieChart,
  BarChart3,
  Clock,
  Eye,
  X
} from 'lucide-react';

interface DashboardStats {
  totalRevenue: number;
  pendingPayments: number;
  occupancyRate: number;
  totalReservations: number;
  activeRooms: number;
  lowStockItems: number;
}

interface PaymentMethod {
  method: string;
  amount: number;
  count: number;
  percentage: number;
}

interface RecentActivity {
  id: number;
  type: 'checkin' | 'checkout' | 'payment' | 'reservation';
  guest_name: string;
  room_number: string;
  amount?: number;
  created_at: string;
}

interface CalendarEvent {
  id: number;
  guest_name: string;
  room_number: string;
  check_in_date: string;
  check_out_date: string;
  status: string;
  total_amount: number;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const isOwner = useIsOwner();
  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0,
    pendingPayments: 0,
    occupancyRate: 0,
    totalReservations: 0,
    activeRooms: 0,
    lowStockItems: 0
  });
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showNewRoomModal, setShowNewRoomModal] = useState(false);
  const [newRoomData, setNewRoomData] = useState({
    room_number: '',
    room_type: '',
    daily_rate: 0,
    max_occupancy: 1,
    amenities: ''
  });
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const [
        financialResponse, 
        roomsResponse, 
        reservationsResponse, 
        inventoryResponse,
        paymentsResponse
      ] = await Promise.all([
        fetch(`/api/dashboard/financial?start_date=${startDate}&end_date=${endDate}`),
        fetch('/api/rooms'),
        fetch('/api/reservations'),
        fetch('/api/inventory'),
        fetch(`/api/payments?start_date=${startDate}&end_date=${endDate}`)
      ]);

      const financial = await financialResponse.json();
      const rooms = await roomsResponse.json();
      const reservations = await reservationsResponse.json();
      const inventory = await inventoryResponse.json();
      const payments = paymentsResponse.ok ? await paymentsResponse.json() : [];

      const lowStockItems = inventory.filter((item: any) => 
        item.current_stock <= item.min_stock && item.is_active
      ).length;

      // Process payment methods
      const paymentMethodStats = payments.reduce((acc: any, payment: any) => {
        const method = payment.payment_method;
        if (!acc[method]) {
          acc[method] = { amount: 0, count: 0 };
        }
        acc[method].amount += payment.amount;
        acc[method].count += 1;
        return acc;
      }, {});

      const totalPayments = payments.reduce((sum: number, p: any) => sum + p.amount, 0);
      const processedPaymentMethods = Object.entries(paymentMethodStats).map(([method, data]: [string, any]) => ({
        method: getPaymentMethodLabel(method),
        amount: data.amount,
        count: data.count,
        percentage: totalPayments > 0 ? (data.amount / totalPayments) * 100 : 0
      }));

      // Process recent activity
      const activity: RecentActivity[] = [];
      
      // Add recent reservations
      reservations.slice(0, 5).forEach((reservation: any) => {
        activity.push({
          id: reservation.id,
          type: 'reservation',
          guest_name: reservation.guest_name,
          room_number: reservation.room_number,
          amount: reservation.total_amount,
          created_at: reservation.created_at
        });
      });

      // Add recent payments
      payments.slice(0, 5).forEach((payment: any) => {
        activity.push({
          id: payment.id,
          type: 'payment',
          guest_name: payment.guest_name || 'N/A',
          room_number: payment.room_number || 'N/A',
          amount: payment.amount,
          created_at: payment.created_at
        });
      });

      // Sort by date and take most recent
      const sortedActivity = activity
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10);

      // Process calendar events (next 7 days)
      const today = new Date();
      const next7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        return date.toISOString().split('T')[0];
      });

      const calendarReservations = reservations.filter((r: any) => 
        next7Days.some(date => 
          date >= r.check_in_date && date <= r.check_out_date
        )
      );

      setStats({
        totalRevenue: financial.totalRevenue || 0,
        pendingPayments: financial.pendingPayments || 0,
        occupancyRate: financial.occupancyRate || 0,
        totalReservations: reservations.length || 0,
        activeRooms: rooms.length || 0,
        lowStockItems
      });

      setPaymentMethods(processedPaymentMethods);
      setRecentActivity(sortedActivity);
      setCalendarEvents(calendarReservations);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingRoom(true);

    try {
      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRoomData),
      });

      if (response.ok) {
        setShowNewRoomModal(false);
        setNewRoomData({
          room_number: '',
          room_type: '',
          daily_rate: 0,
          max_occupancy: 1,
          amenities: ''
        });
        fetchDashboardData(); // Refresh data
      }
    } catch (error) {
      console.error('Error creating room:', error);
    } finally {
      setIsCreatingRoom(false);
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: { [key: string]: string } = {
      'cash': 'Dinheiro',
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'pix': 'PIX',
      'bank_transfer': 'Transferência'
    };
    return labels[method] || method;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'checkin': return <Users className="w-4 h-4" />;
      case 'checkout': return <Clock className="w-4 h-4" />;
      case 'payment': return <CreditCard className="w-4 h-4" />;
      case 'reservation': return <Calendar className="w-4 h-4" />;
      default: return <Eye className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'checkin': return 'text-green-600 bg-green-100 dark:bg-green-900/20';
      case 'checkout': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
      case 'payment': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/20';
      case 'reservation': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, change, format = 'number' }: {
    title: string;
    value: number;
    icon: any;
    color: string;
    change?: number;
    format?: 'number' | 'currency' | 'percentage';
  }) => {
    const formatValue = (val: number) => {
      switch (format) {
        case 'currency':
          return new Intl.NumberFormat('pt-BR', { 
            style: 'currency', 
            currency: 'BRL' 
          }).format(val);
        case 'percentage':
          return `${val}%`;
        default:
          return val.toLocaleString('pt-BR');
      }
    };

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-all hover:shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
              {loading ? '-' : formatValue(value)}
            </p>
            {change !== undefined && (
              <p className={`text-sm mt-2 flex items-center ${
                change >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                <TrendingUp className={`w-4 h-4 mr-1 ${change < 0 ? 'rotate-180' : ''}`} />
                {Math.abs(change)}% vs período anterior
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header Simples */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Dashboard {isOwner ? 'Administrativo' : 'Recepção'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {isOwner 
                  ? 'Visão geral completa do seu hotel' 
                  : 'Painel de controle da recepção'
                }
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="7">Últimos 7 dias</option>
                <option value="30">Últimos 30 dias</option>
                <option value="90">Últimos 90 dias</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Receita - apenas para proprietário */}
          {permissions.canViewRevenue && (
            <StatCard
              title="Receita Total"
              value={stats.totalRevenue}
              icon={DollarSign}
              color="bg-green-500"
              format="currency"
              change={12}
            />
          )}
          
          {/* Pagamentos Pendentes - apenas para proprietário */}
          {permissions.canViewRevenue && (
            <StatCard
              title="Pagamentos Pendentes"
              value={stats.pendingPayments}
              icon={CreditCard}
              color="bg-yellow-500"
              format="currency"
              change={-5}
            />
          )}
          
          {/* Taxa de Ocupação - todos podem ver */}
          <StatCard
            title="Taxa de Ocupação"
            value={stats.occupancyRate}
            icon={Users}
            color="bg-blue-500"
            format="percentage"
            change={8}
          />
          
          {/* Reservas Ativas - todos podem ver */}
          <StatCard
            title="Reservas Ativas"
            value={stats.totalReservations}
            icon={Calendar}
            color="bg-purple-500"
            change={15}
          />
          
          {/* Para recepcionistas, mostrar cards adicionais */}
          {!isOwner && (
            <StatCard
              title="Quartos Disponíveis"
              value={stats.activeRooms - Math.round((stats.occupancyRate / 100) * stats.activeRooms)}
              icon={Home}
              color="bg-indigo-500"
              change={0}
            />
          )}
        </div>


        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Payment Methods - apenas para proprietário */}
          {permissions.canViewFinancialReports && (
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Formas de Pagamento</h3>
                  <PieChart className="w-5 h-5 text-gray-400" />
                </div>
              <div className="space-y-4">
                {paymentMethods.map((method, index) => (
                  <div key={method.method} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ 
                          backgroundColor: `hsl(${index * 60}, 70%, 50%)` 
                        }}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{method.method}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(method.amount)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {method.percentage.toFixed(1)}% ({method.count} pagamentos)
                      </p>
                    </div>
                  </div>
                ))}
                {paymentMethods.length === 0 && !loading && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    Nenhum pagamento no período
                  </p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Calendar Widget */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Próximas Reservas</h3>
                <Calendar className="w-5 h-5 text-gray-400" />
              </div>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {calendarEvents.map((event) => (
                  <div 
                    key={event.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {event.guest_name}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          • Quarto {event.room_number}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(event.check_in_date).toLocaleDateString('pt-BR')} - {new Date(event.check_out_date).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        event.status === 'confirmed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                        event.status === 'checked_in' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                      }`}>
                        {event.status === 'confirmed' ? 'Confirmado' : 
                         event.status === 'checked_in' ? 'Check-in' : event.status}
                      </span>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(event.total_amount)}
                      </p>
                    </div>
                  </div>
                ))}
                {calendarEvents.length === 0 && !loading && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                    Nenhuma reserva nos próximos dias
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity & Room Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Recent Activity */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Atividade Recente</h3>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {recentActivity.map((activity, index) => (
                <div key={`${activity.type}-${activity.id}-${index}`} className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${getActivityColor(activity.type)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {activity.guest_name} • Quarto {activity.room_number}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(activity.created_at).toLocaleDateString('pt-BR')}
                      {activity.amount && (
                        <span className="ml-2 font-medium">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(activity.amount)}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
              {recentActivity.length === 0 && !loading && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  Nenhuma atividade recente
                </p>
              )}
            </div>
          </div>

          {/* Room Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Status dos Quartos</h3>
              <Home className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600 dark:text-gray-400">Disponíveis</span>
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {stats.activeRooms - Math.round((stats.occupancyRate / 100) * stats.activeRooms)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-gray-600 dark:text-gray-400">Ocupados</span>
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {Math.round((stats.occupancyRate / 100) * stats.activeRooms)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-600 dark:text-gray-400">Total</span>
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">{stats.activeRooms}</span>
              </div>
              
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <Package className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Estoque Baixo</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {stats.lowStockItems > 0 && (
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`font-semibold ${
                      stats.lowStockItems > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {stats.lowStockItems}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Ações Rápidas
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <button 
              onClick={() => setShowReservationModal(true)}
              className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-left group"
            >
              <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2 group-hover:scale-110 transition-transform" />
              <div className="font-medium text-gray-900 dark:text-white">Nova Reserva</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Criar nova reserva</div>
            </button>
            
            <button 
              onClick={() => navigate('/reservations')}
              className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors text-left group"
            >
              <Users className="w-6 h-6 text-green-600 dark:text-green-400 mb-2 group-hover:scale-110 transition-transform" />
              <div className="font-medium text-gray-900 dark:text-white">Check-in</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Realizar check-in</div>
            </button>
            
            <button 
              onClick={() => navigate('/rooms')}
              className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors text-left group"
            >
              <Home className="w-6 h-6 text-purple-600 dark:text-purple-400 mb-2 group-hover:scale-110 transition-transform" />
              <div className="font-medium text-gray-900 dark:text-white">Quartos</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Gerenciar quartos</div>
            </button>
            
            <button 
              onClick={() => navigate('/inventory')}
              className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors text-left group"
            >
              <Package className="w-6 h-6 text-orange-600 dark:text-orange-400 mb-2 group-hover:scale-110 transition-transform" />
              <div className="font-medium text-gray-900 dark:text-white">Estoque</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Gerenciar estoque</div>
            </button>
            
            <button 
              onClick={() => navigate('/reports')}
              className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors text-left group"
            >
              <BarChart3 className="w-6 h-6 text-indigo-600 dark:text-indigo-400 mb-2 group-hover:scale-110 transition-transform" />
              <div className="font-medium text-gray-900 dark:text-white">Relatórios</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Ver relatórios</div>
            </button>
          </div>
        </div>

        {/* Reservation Modal */}
        {showReservationModal && (
          <ReservationModal
            isOpen={showReservationModal}
            onClose={() => setShowReservationModal(false)}
            onRefresh={fetchDashboardData}
          />
        )}

        {/* New Room Modal */}
        {showNewRoomModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowNewRoomModal(false)} />
              
              <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Novo Quarto
                  </h3>
                  <button
                    onClick={() => setShowNewRoomModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleCreateRoom} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Número do Quarto
                    </label>
                    <input
                      type="text"
                      value={newRoomData.room_number}
                      onChange={(e) => setNewRoomData({ ...newRoomData, room_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo do Quarto
                    </label>
                    <input
                      type="text"
                      value={newRoomData.room_type}
                      onChange={(e) => setNewRoomData({ ...newRoomData, room_type: e.target.value })}
                      placeholder="Ex: Standard, Deluxe, Suíte"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Diária (R$)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newRoomData.daily_rate}
                      onChange={(e) => setNewRoomData({ ...newRoomData, daily_rate: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Capacidade Máxima
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newRoomData.max_occupancy}
                      onChange={(e) => setNewRoomData({ ...newRoomData, max_occupancy: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Comodidades
                    </label>
                    <textarea
                      value={newRoomData.amenities}
                      onChange={(e) => setNewRoomData({ ...newRoomData, amenities: e.target.value })}
                      placeholder="Ex: WiFi, TV, Ar condicionado, Frigobar"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Separe as comodidades por vírgula
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowNewRoomModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isCreatingRoom}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCreatingRoom ? 'Criando...' : 'Criar Quarto'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
